import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import LegoAdminPageContext from '../../pages/legoAdminPageContext';
import BadgeColorHash from '../common/BadgeColorHash';
import LegoContextSummary from '../lego/LegoContextSummary';
import LegoEditButton from '../lego/LegoEditButton';
import { LegoStateBadge } from '../lego/LegoStateBadge';
import { ImagePreview } from './ImagePreview';
import PublishedFuseboxThumbnail from '../lego/fusebox-editor/PublishedFuseboxThumbnail';
import urls from '../../../../components/interop/urls';
import { stringToColor } from '../common/colorUtils';
import { TypeTag } from '../lego/TypeTag';

function urlFuseboxThumbnail(modelId, year, other, fuseIndex) {
  // TODO: NASTY. Must be done in server with proper character sanitization
  let [marcaUrlSafe, modeloUrlSafe] = (modelId || '').toLowerCase().split('-');
  const modelObj = { idModelo: modelId, marcaUrlSafe, modeloUrlSafe };
  return urls.urlFuseboxThumbnail(modelObj, year, other, fuseIndex);
}

export default function ModalClusterReferences({ clusterIds }) {
  const { page } = useContext(LegoAdminPageContext);
  const [legos, setLegos] = useState(null);
  const [error, setError] = useState(null);

  const fetchLegos = () => {
    page.service('services/legos').find({
      query: {
        $or: [{ type: 'fusebox' }, { type: 'fuseboxTemplate' }],
        'data.imageClusters': { $in: clusterIds },
        $select: { 'data.fuses': false, 'data.boxDescription': false },
      }
    })
      .then(setLegos)
      .catch((err) => setError(String(err?.message || err)));
  };

  useEffect(fetchLegos, [clusterIds]);

  if (error) return <div className={'alert alert-danger m-3'}>{error}</div>;
  if (!legos) return <div className={'text-secondary p-4'}>Finding fusebox references…</div>;

  return <div>
    {_.map(clusterIds, clusterId => {
      const refs = [];
      _.each(legos, lego => _.each(lego.data || [], (box, fuseIndex) => {
        if (_.some(box.imageClusters, id => String(id) === String(clusterId))) {
          refs.push({ lego, box, fuseIndex });
        }
      }));

      return <div key={clusterId} className={'rounded p-1 mb-2'} style={{ border: `4px solid ${stringToColor(clusterId, { white: 0.5 })}` }}>
        <div className={'mb-1'}>
          Cluster <BadgeColorHash id={clusterId} />
          <span className={'ml-2 text-secondary small'}>{refs.length} lego reference{refs.length === 1 ? '' : 's'}</span>
        </div>


        <div className={'d-flex flex-wrap gap-1'} style={{gap: '5px'}}>
          {refs.length ? _.map(refs, ({ lego, box, fuseIndex }) => {
            const { modelId, year, ...otherContextSpecs } = lego.context?.[0] || {};
            const isPublishedFusebox = lego.type === 'fusebox' && lego.state === 'published';
            const thumbnailUrl = box.boxDiagramImg?.url || urlFuseboxThumbnail(modelId, year, otherContextSpecs, fuseIndex);
            const bgColor = isPublishedFusebox ? ' bg-light-success' : (lego.type === 'fuseboxTemplate' ? 'bg-light-gris-blue' : 'bg-light-secondary translucent');
            return <span key={`${lego._id}_${fuseIndex}`} className={`inline-block align-top text-center rounded p-1 ${bgColor}`}>
              <div className={'text-left'}>
                <LegoEditButton legoId={lego._id} lego={lego} onRefresh={fetchLegos}>
                  <BadgeColorHash id={lego._id} />
                </LegoEditButton>

                {lego.type === 'fuseboxTemplate' ? <TypeTag type={lego.type} /> : null}

                {lego.state !== 'published' ? <span className={'ml-1 zoom-75'}><LegoStateBadge state={lego.state} /></span> : null}
              </div>

              <div className={'text-left'} style={{ maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <LegoContextSummary context={_.map(lego.context, ctx => _.pick(ctx, ['modelId', 'year']))} limit={3} />
              </div>

              <div className={'my-1'}>
                {isPublishedFusebox
                  ? <PublishedFuseboxThumbnail context={lego.context[0]} locale={lego.locales?.[0]} boxIndex={fuseIndex} />
                  : <ImagePreview url={thumbnailUrl} img100 contain zoom />}
              </div>

            </span>;
          }) : <div className={'text-secondary'}>No fusebox or template references this cluster</div>}
        </div>
      </div>;
    })}
  </div>;
}
